import type { Dayjs } from 'dayjs'

export const DATE_RANGE_FORMAT = 'DD.MM.YYYY'

const DAYS_IN_GRID = 42

const MONTH_NAMES = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]

const MONTH_SHORT_NAMES = [
  'Янв',
  'Фев',
  'Мар',
  'Апр',
  'Май',
  'Июн',
  'Июл',
  'Авг',
  'Сен',
  'Окт',
  'Ноя',
  'Дек',
]

export const WEEKDAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const YEARS_IN_GRID = 12
const YEARS_IN_DECADE = 10

export type CalendarView = 'days' | 'months' | 'years'

export type PeriodCell = {
  key: string
  label: string
  date: Dayjs
  isOutside: boolean
  isCurrent: boolean
  isSelected: boolean
  isDisabled: boolean
}

export type DateRangeValue = {
  from: Dayjs | null
  to: Dayjs | null
}

export type CalendarDayState = {
  isOutside: boolean
  isToday: boolean
  isSelected: boolean
  isInRange: boolean
  isRangeEdge: boolean
  isDisabled: boolean
}

export const formatMonthTitle = (month: Dayjs) =>
  `${MONTH_NAMES[month.month()]} ${month.year()}`

export const buildMonthGrid = (month: Dayjs): Dayjs[] => {
  const firstDay = month.startOf('month')
  const offsetFromMonday = (firstDay.day() + 6) % 7
  const gridStart = firstDay.subtract(offsetFromMonday, 'day')
  return Array.from({ length: DAYS_IN_GRID }, (_, index) =>
    gridStart.add(index, 'day')
  )
}

const isOrderedRange = (range: DateRangeValue) =>
  range.from !== null &&
  range.to !== null &&
  !range.from.isAfter(range.to, 'day')

export const getCalendarDayState = (
  day: Dayjs,
  params: {
    month: Dayjs
    value: Dayjs | null
    range: DateRangeValue
    today: Dayjs
    maxDate: Dayjs | null
  }
): CalendarDayState => {
  const { month, value, range, today, maxDate } = params
  const isRangeEdge =
    isOrderedRange(range) &&
    (day.isSame(range.from, 'day') || day.isSame(range.to, 'day'))

  return {
    isOutside: !day.isSame(month, 'month'),
    isToday: day.isSame(today, 'day'),
    isSelected: value !== null && day.isSame(value, 'day'),
    isInRange:
      isOrderedRange(range) &&
      day.isAfter(range.from, 'day') &&
      day.isBefore(range.to, 'day'),
    isRangeEdge,
    isDisabled: maxDate !== null && day.isAfter(maxDate, 'day'),
  }
}

const getDecadeStart = (year: number) =>
  Math.floor(year / YEARS_IN_DECADE) * YEARS_IN_DECADE

const VIEW_STEP: Record<
  CalendarView,
  { amount: number; unit: 'month' | 'year' }
> = {
  days: { amount: 1, unit: 'month' },
  months: { amount: 1, unit: 'year' },
  years: { amount: YEARS_IN_DECADE, unit: 'year' },
}

export const shiftCalendarView = (
  month: Dayjs,
  view: CalendarView,
  direction: 1 | -1
) => month.add(direction * VIEW_STEP[view].amount, VIEW_STEP[view].unit)

export const getCalendarViewTitle = (month: Dayjs, view: CalendarView) => {
  if (view === 'days') return formatMonthTitle(month)
  if (view === 'months') return String(month.year())
  const decadeStart = getDecadeStart(month.year())
  return `${decadeStart} – ${decadeStart + YEARS_IN_DECADE - 1}`
}

export const isNextViewDisabled = (
  month: Dayjs,
  view: CalendarView,
  maxDate: Dayjs | null
) => {
  if (maxDate === null) return false
  if (view === 'days') return !month.isBefore(maxDate, 'month')
  if (view === 'months') return month.year() >= maxDate.year()
  return getDecadeStart(month.year()) + YEARS_IN_DECADE > maxDate.year()
}

export const buildMonthCells = (
  month: Dayjs,
  params: { value: Dayjs | null; today: Dayjs; maxDate: Dayjs | null }
): PeriodCell[] =>
  MONTH_SHORT_NAMES.map((label, index) => {
    const date = month.month(index).startOf('month')
    return {
      key: date.format('YYYY-MM'),
      label,
      date,
      isOutside: false,
      isCurrent: date.isSame(params.today, 'month'),
      isSelected: params.value !== null && date.isSame(params.value, 'month'),
      isDisabled:
        params.maxDate !== null && date.isAfter(params.maxDate, 'month'),
    }
  })

export const buildYearCells = (
  month: Dayjs,
  params: { value: Dayjs | null; today: Dayjs; maxDate: Dayjs | null }
): PeriodCell[] => {
  const decadeStart = getDecadeStart(month.year())
  const firstYear = decadeStart - (YEARS_IN_GRID - YEARS_IN_DECADE) / 2
  return Array.from({ length: YEARS_IN_GRID }, (_, index) => {
    const year = firstYear + index
    const date = month.year(year)
    return {
      key: String(year),
      label: String(year),
      date,
      isOutside: year < decadeStart || year >= decadeStart + YEARS_IN_DECADE,
      isCurrent: year === params.today.year(),
      isSelected: params.value !== null && year === params.value.year(),
      isDisabled: params.maxDate !== null && year > params.maxDate.year(),
    }
  })
}
